import { Box } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useCloudProvider } from '@root/components/v2/content/CloudProviderBar/CloudProviderContext';
import LastUpdate from '@root/components/v2/content/LastUpdate';
import Breadcrumbs from '@root/components/v2/global/breadcrumbs';
import PageContainer from '@root/components/v2/layout/PageContainer';
import PageHeader from '@root/components/v2/layout/PageHeader';
import {
  K8S_VULNERABILITIES_SUB_TABS,
  K8S_VULNERABILITIES_TABS,
  k8sVulnerabilitiesSubTabPath,
  resolveK8sVulnerabilitiesSubTab,
} from '@root/constants/moduleTabs';
import useLatestScanTime from '@root/hooks/useLatestScanTime';
import { useQuery } from '@root/hooks/useQuery';
import Dashboard from './Dashboard';
import Host from './Host';
import RiskSpotlightToggle from './RiskSpotlight';
import SBOM from './SBOM';
import VulnerabilitiesTab from './VulnerabilitiesTab';

const BREADCRUMBS = [{ title: 'Kubernetes Vulnerabilities' }];

const pages = {
  dashboard: Dashboard,
  vulnerabilities: VulnerabilitiesTab,
  sbom: SBOM,
  host: Host,
};

// The `type` RiskSpotlightToggle fetches its summary for, keyed by the view on screen (a module tab,
// or a sub tab of Vulnerabilities); views missing here have no spotlight.
const RISK_SPOTLIGHT_TYPES = { findings: 0, cves: 0, workloads: 1, images: 2, sbom: 3 };

/**
 * The canonical URL for a tab selection, keeping every other param (filters, breadcrumbs). The
 * Vulnerabilities tab always names its sub tab; the other tabs never carry one.
 */
const canonicalPath = (activeTab, subTab, search) => {
  const params = new URLSearchParams(search);
  if (activeTab === 'vulnerabilities') return k8sVulnerabilitiesSubTabPath(subTab, params);
  params.delete(K8S_VULNERABILITIES_SUB_TABS.tabParam);
  return K8S_VULNERABILITIES_TABS.path(activeTab, params);
};

const K8Vulnerabilities = () => {
  const { cloudProvider } = useCloudProvider();
  const { latestK8sScanTime } = useLatestScanTime();
  const query = useQuery();
  const history = useHistory();
  const unscannedImages = query.get('unscannedImages');
  const activeTab = useMemo(() => K8S_VULNERABILITIES_TABS.resolve(query.toString()), [query]);
  const subTab = useMemo(() => resolveK8sVulnerabilitiesSubTab(query.toString()), [query]);

  const [isEnabled, setIsEnabled] = useState(false);

  // Pin the resolved tabs in the URL, so a bare link or a legacy `activeTab` (`cves`, `0`, …) reads as
  // the tab and sub tab it landed on, and the header tabs highlight it. The canonical URL maps onto
  // itself, so this settles after one replace.
  useEffect(() => {
    const target = canonicalPath(activeTab, subTab, query.toString());
    if (target !== `${K8S_VULNERABILITIES_TABS.basePath}?${query.toString()}`) history.replace(target);
  }, [activeTab, history, query, subTab]);

  const Page = pages[activeTab];
  const riskSpotlightType = RISK_SPOTLIGHT_TYPES[activeTab === 'vulnerabilities' ? subTab : activeTab];
  const riskSpotlight =
    riskSpotlightType !== undefined && !unscannedImages ? (
      <RiskSpotlightToggle setIsEnabled={setIsEnabled} isEnabled={isEnabled} type={riskSpotlightType} />
    ) : null;

  return (
    <PageContainer>
      <PageHeader
        title={<Breadcrumbs breadcrumbs={BREADCRUMBS} />}
        breadcrumb={true}
        path={canonicalPath(activeTab, subTab)}>
        <Box mt={1}>
          <LastUpdate date={cloudProvider !== 'azure' && latestK8sScanTime ? new Date(latestK8sScanTime) : null} />
        </Box>
      </PageHeader>
      {/* Vulnerabilities places the spotlight itself, under its sub tab strip. */}
      {activeTab !== 'vulnerabilities' && riskSpotlight}
      <Page isEnabled={isEnabled} riskSpotlight={riskSpotlight} subTab={subTab} />
    </PageContainer>
  );
};

export default K8Vulnerabilities;
